/**
 * Removes the last empty parameter from an array of parameters or a string.
 *
 * @template T Type of input (string or string array)
 * @param {T} params Input parameters
 * @returns {T | string[]} Parameters with last empty element removed if applicable
 * @example
 * removeLastEmptyParam(['a', 'b', '']); // Returns ['a', 'b']
 * removeLastEmptyParam('example'); // Returns 'example'
 */
export const removeLastEmptyParam = <T extends string | string[]>(
  params: T
): T | string[] =>
  Array.isArray(params) && params?.at(-1) === '' ? params.slice(0, -1) : params

/**
 * Converts an absolute URL to a relative URL by extracting pathname, search, and hash.
 *
 * @param {string} url The absolute URL to convert
 * @returns {string} The relative URL with pathname, search, and hash
 * @throws {TypeError} If the URL is invalid
 * @throws {Error} If the URL cannot be parsed
 * @example
 * toRelativeUrl('https://example.com/path?query=value#hash');
 * // Returns '/path?query=value#hash'
 */
export const toRelativeUrl = (url: string): string => {
  const { pathname, search, hash } = new URL(url)
  return `${pathname}${search}${hash}`.replaceAll(/,/g, '')
}

/**
 * Determines if the given route represents a Product Detail Page (PDP).
 * Uses the same slug convention as fetchPage: last segment must contain `-cod-`
 * with exactly three segments in the code part.
 *
 * @param route - Route object with params.slug (e.g. Nuxt route `to`)
 * @returns True if the route is a valid product page, false otherwise
 */
export const isProductRoute = (route: {
  params: { slug?: string | string[] }
}): boolean => {
  const slug = removeLastEmptyParam(route.params.slug!)
  const slugArray = Array.isArray(slug) ? slug : [slug]
  const productSlug = slugArray.at(-1)

  if (!productSlug) return false
  const code = (productSlug as string).split('-cod-')[1]
  if (!code) return false
  const segments = code.split('-')
  return segments.length === 3
}
